import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { AddRewardToCart, AddRewardToCartVariables } from "../../../types/__generated__/AddRewardToCart";
import { ADD_REWARD_TO_BOX, ADD_REWARD_TO_CART } from "../../../graphql/resolvers/reward";
import { AddRewardToBox, AddRewardToBoxVariables } from "../../../types/__generated__/AddRewardToBox";
import { logError } from "../../../helpers/logging";
import { showErrorAlert, showSuccessAlert } from "../../../helpers/alerts";
import { I18N_NAMESPACES } from "../../../helpers/constants";
import { colors } from "../../../styles/variables";

import RewardMerchDetailsInfo from "../../molecules/RewardMerchDetailsInfo";
import { GET_MEMBER_REWARD_BASIC_INFO } from "../../../graphql/resolvers/member";
import {
  ProductItemById_productById as Product,
  ProductItemById_productById_variants as ProductVariant,
} from "../../../types/__generated__/ProductItemById";
import { filterEmptyValues } from "../../../helpers/types.helper";

import { AddToBoxOrCartSuccessMessage } from "../../atoms/AddToBoxOrCartSuccessMessage";
import { useLayoutContext } from "../Layout/context";
import PageMeta from "../../molecules/PageMeta";
import { useBuildYourOwnAddGuard } from "../../../helpers/buildYourOwnCartLock";

interface RewardMerchDetailsControllerProps {
  variant: "small" | "large" | "fullPage";
  memberId: number | undefined | null;
  product: Product;
  memberLevel: number | null | undefined;
  memberPointsBalance: number | undefined | null;
  imageGalleryWidth: string;
  isTopOffSetEqualToHeader?: boolean;
  isOnMonthlyBoxToggle: boolean | undefined;
  selectedProductVariant?: Product | ProductVariant | null | undefined;
  onAddMerchSuccess?: () => void;
}

const RewardMerchDetailsController = ({
  memberId,
  product,
  memberLevel,
  memberPointsBalance,
  imageGalleryWidth,
  variant,
  isTopOffSetEqualToHeader = true,
  isOnMonthlyBoxToggle,
  selectedProductVariant,
  onAddMerchSuccess,
}: RewardMerchDetailsControllerProps) => {
  const { t } = useTranslation([I18N_NAMESPACES.COMMON, I18N_NAMESPACES.REWARDS]);
  const { setShouldOpenBoxAndCartTray } = useLayoutContext();
  const [selectedCharacteristicValueIds, setSelectedCharacteristicValueIds] = useState<{
    [characteristicId: string]: number;
  }>({});

  const productVariantsLength = product?.variants?.length ?? 0;
  // The product to add when user clicks on the add button
  // If the product has variants, this will be undefined until the user selects a variant
  const [productToAdd, setProductToAdd] = useState<Product | ProductVariant | null | undefined>(
    selectedProductVariant || productVariantsLength > 0 ? undefined : product
  );

  const [addRewardToCart, { loading: isAddingToCart }] = useMutation<
    AddRewardToCart,
    AddRewardToCartVariables
  >(ADD_REWARD_TO_CART);
  const isBlockedByBuildYourOwn = useBuildYourOwnAddGuard();
  const [addRewardToBox, { loading: isAddingToBox }] = useMutation<AddRewardToBox, AddRewardToBoxVariables>(
    ADD_REWARD_TO_BOX
  );

  const unlockLevel = product.unlockedAtLevel?.rank;
  const isAddToBoxCartMutationLoading = isAddingToBox || isAddingToCart;

  /**
   * Shows reward as locked if it is unlocked at a specific level that the current member has not reached yet.
   */
  const merchUnlockLevel = useMemo(() => {
    if (unlockLevel && (!memberLevel || memberLevel < unlockLevel)) {
      return unlockLevel;
    }
    return null;
  }, [unlockLevel, memberLevel]);
  const hasTooFewPoints =
    (memberPointsBalance ?? 0) < (product.pointCost ?? 1) && product.isRedeemableWithPoints;
  const isLocked = !!merchUnlockLevel;
  const isAddButtonGrayed =
    productToAdd === undefined || hasTooFewPoints || !productToAdd?.hasStock || isLocked;
  const isAddButtonDisabled = isAddToBoxCartMutationLoading;

  /**
   * Returns the product variant corresponding to the given characteristic values.
   */
  const getProductVariantByCharacteristicValues = (characteristicValues: {
    [characteristicId: string]: number;
  }): ProductVariant | undefined =>
    product?.variants.find(({ productCharacteristicValues }) =>
      productCharacteristicValues.every(
        ({ id, productCharacteristic }) => characteristicValues[productCharacteristic.id!] === id
      )
    );

  useEffect(() => {
    if (selectedProductVariant) {
      setProductToAdd(selectedProductVariant);
      return;
    }
    if (product.variants.length > 0) {
      const productVariant = getProductVariantByCharacteristicValues(selectedCharacteristicValueIds);
      !!productVariant && setProductToAdd(productVariant);
    }
  }, [selectedCharacteristicValueIds, selectedProductVariant]);

  /**
   * Formats the characteristic values to be used in the characteristics selector.
   */
  const formattedCharacteristics = useMemo(
    () =>
      product.characteristics.map((characteristic) => ({
        id: characteristic.id!,
        name: characteristic.name,
        values: characteristic.values.map((value) => {
          // Get the product variant matching this characteristic value selection
          const productVariant = getProductVariantByCharacteristicValues({
            ...selectedCharacteristicValueIds,
            [characteristic.id!]: value.id!,
          });
          let context;
          if (!productVariant) {
            context = undefined;
          } else if (!productVariant.hasStock) {
            context = "outOfStock";
          } else if (productVariant.isLowStock) {
            context = "lowStock";
          }
          return {
            id: value.id!,
            name: t("rewards:productCharacteristicValue", {
              name: value.name,
              context,
            }),
            isSelected: value.id === selectedCharacteristicValueIds[characteristic.id!],
          };
        }),
      })),
    [product.characteristics, selectedCharacteristicValueIds]
  );

  const handleCharacteristicValueSelect = (characteristicId: number, valueId: number) => {
    setSelectedCharacteristicValueIds((prevSelectedCharacteristicValueIds) => ({
      ...prevSelectedCharacteristicValueIds,
      [characteristicId]: valueId,
    }));
  };

  const validateAddButtonClick = (): boolean => {
    if (!productToAdd?.id) {
      showErrorAlert(
        t("rewards:pleaseSelectCharacteristic", {
          characteristics: product.characteristics.map((c) => c.name.toLowerCase()).join(" and "),
        }),
        { autoClose: 10000 }
      );
      return false;
    }
    if (!productToAdd.hasStock) {
      showErrorAlert(t("common:sorryNoStock"), { autoClose: 10000 });
      return false;
    }
    if (merchUnlockLevel) {
      showErrorAlert(t("rewards:locked", { unlockLevel }), { autoClose: 10000 });
      return false;
    }
    if (hasTooFewPoints) {
      showErrorAlert(t("rewards:notEnoughPoints"), { autoClose: 10000 });
      return false;
    }
    return true;
  };

  const handleClickAddRewardButton = async (redeemWithPoints: boolean) => {
    if (!validateAddButtonClick() || !productToAdd?.id) return;
    try {
      let errorMessage;
      if (isOnMonthlyBoxToggle) {
        const { data: addRewardData, errors: addRewardDataError } = await addRewardToBox({
          variables: {
            productId: productToAdd?.id,
            quantity: 1,
            redeemWithPoints,
          },
          refetchQueries: [{ query: GET_MEMBER_REWARD_BASIC_INFO }],
        });
        errorMessage =
          addRewardData?.addProductToNextBox?.errors?.[0]?.messages?.[0] ??
          (addRewardDataError?.[0]?.message ? t("common:sorry") : null);
      } else {
        if (!memberId) {
          throw Error();
        }
        // Reward merch (and its variants) is never wine, so a build-your-own order cannot take it.
        if (isBlockedByBuildYourOwn([{ product: { productType: null }, quantity: 1 }])) return;
        const { data: addRewardData, errors: addRewardDataError } = await addRewardToCart({
          variables: {
            input: {
              productId: productToAdd.id,
              quantity: 1,
              redeemWithPoints,
              memberId,
            },
          },
          refetchQueries: redeemWithPoints ? [{ query: GET_MEMBER_REWARD_BASIC_INFO }] : [],
        });
        errorMessage =
          addRewardData?.addShoppingCartItem?.errors?.[0]?.messages?.[0] ??
          (addRewardDataError?.[0]?.message ? t("common:sorry") : null);
      }

      if (errorMessage) {
        showErrorAlert(errorMessage, { autoClose: 10000 });
      }
      if (!errorMessage) {
        const successMessage = (
          <AddToBoxOrCartSuccessMessage
            i18nKey={
              isOnMonthlyBoxToggle ? "common:productAddedToMonthlyBox" : "common:productAddedToOneOffCart"
            }
            values={{
              product: productToAdd.name,
              interpolation: { escapeValue: false },
            }}
            setShouldOpenBoxAndCartTray={setShouldOpenBoxAndCartTray}
          />
        );
        showSuccessAlert(successMessage, {});
        onAddMerchSuccess && onAddMerchSuccess();
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      logError(error, { location: "RewardMerchDetailsController" });
      showErrorAlert(t("common:sorry"), { autoClose: 10000 });
    }
  };

  const imageCarouselBackgroundColor = colors.merchCardDefaultBackgroundColor;
  const galleryPhotos: string[] =
    product.productPhotos.map((productPhoto) => productPhoto.photoLarge).filter(filterEmptyValues) ?? [];

  if (variant === "fullPage" && product?.id) {
    return (
      <>
        <PageMeta
          title={t("common:pageMetaTitle", {
            title: product?.name,
            interpolation: { escapeValue: false },
          })}
          image={product?.coverPhotoMedium ?? ""}
        />
        <RewardMerchDetailsInfo
          isAddButtonDisabled={isAddButtonDisabled}
          handleAddButtonClick={() => validateAddButtonClick() && handleClickAddRewardButton(true)}
          productUrlPhotos={galleryPhotos}
          productName={product.name}
          pointCost={product.pointCost}
          sellingPrice={product.sellingPrice}
          // We only support point redemption in the reward store
          canBePurchasedWithCash={false}
          isRedeemableWithPoints={product.isRedeemableWithPoints}
          imageGalleryWidth={imageGalleryWidth}
          descriptionMarkdown={product.descriptionMarkdown}
          variant={variant}
          isAddButtonGrayed={isAddButtonGrayed}
          isTopOffSetEqualToHeader={isTopOffSetEqualToHeader}
          imageCarouselBackgroundColor={imageCarouselBackgroundColor}
          isLocked={isLocked}
          characteristics={formattedCharacteristics}
          onCharacteristicValueSelect={handleCharacteristicValueSelect}
        />
      </>
    );
  }

  return (
    <RewardMerchDetailsInfo
      isAddButtonDisabled={isAddButtonDisabled}
      handleAddButtonClick={() => validateAddButtonClick() && handleClickAddRewardButton(true)}
      productUrlPhotos={galleryPhotos}
      productName={product.name}
      pointCost={product.pointCost}
      sellingPrice={product.sellingPrice}
      // We only support point redemption in the reward store
      canBePurchasedWithCash={false}
      isRedeemableWithPoints={product.isRedeemableWithPoints}
      imageGalleryWidth={imageGalleryWidth}
      descriptionMarkdown={product.descriptionMarkdown}
      variant={variant}
      isAddButtonGrayed={isAddButtonGrayed}
      isTopOffSetEqualToHeader={isTopOffSetEqualToHeader}
      imageCarouselBackgroundColor={imageCarouselBackgroundColor}
      isLocked={isLocked}
      characteristics={formattedCharacteristics}
      onCharacteristicValueSelect={handleCharacteristicValueSelect}
    />
  );
};

export default RewardMerchDetailsController;
